import { useState } from 'react';
import { Check, AlertTriangle, ExternalLink, Copy, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { reachGoal, GOAL_TRADE_URL_SET } from '@/lib/metrika';

interface TradeUrlInputProps {
  value: string;
  onChange: (value: string) => void;
  onSave: () => Promise<void>;
}

export function TradeUrlInput({ value, onChange, onSave }: TradeUrlInputProps) {
  // For new users (no value) — start in edit mode so they can enter and save.
  // For existing users (has value) — start in "configured" view.
  const [isEditing, setIsEditing] = useState(!value);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const isValidFormat = (url: string) => {
    return url.startsWith('https://steamcommunity.com/tradeoffer/new/');
  };

  const handleSave = async () => {
    if (!isValidFormat(value)) return;
    
    setIsSaving(true);
    try {
      await onSave();
      reachGoal(GOAL_TRADE_URL_SET);
      setSaved(true);
      setIsEditing(false);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save trade URL:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
  };

  if (!isEditing && value) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-[#0d1117] rounded-lg px-4 py-3 border border-[#30363d] flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
            <span className="text-white truncate flex-1">
              Trade URL настроен
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCopy}
              className="h-8 w-8 text-[#8b949e] hover:text-white"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <Button
            variant="outline"
            onClick={() => setIsEditing(true)}
            className="border-[#30363d] text-[#8b949e] hover:text-white"
          >
            Изменить
          </Button>
        </div>
        
        {saved && (
          <Alert className="bg-green-500/10 border-green-500/30">
            <Check className="w-4 h-4 text-green-500" />
            <AlertDescription className="text-green-400">
              Trade URL успешно сохранен
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm text-[#8b949e]">
          Trade URL для обмена
        </label>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://steamcommunity.com/tradeoffer/new/?partner=...&token=..."
          className="bg-[#0d1117] border-[#30363d] text-white placeholder:text-[#8b949e] focus:border-[#ff6b00]"
        />
        
        {value && !isValidFormat(value) && (
          <p className="text-red-400 text-xs flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Неверный формат Trade URL
          </p>
        )}
        {value && isValidFormat(value) && !saved && (
          <p className="text-[#8b949e] text-xs">
            Нажмите кнопку "Сохранить" или кликните вне поля для подтверждения
          </p>
        )}
        {saved && (
          <p className="text-green-400 text-xs flex items-center gap-1">
            <Check className="w-3 h-3" />
            Trade URL сохранён
          </p>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Button
          onClick={handleSave}
          disabled={!isValidFormat(value) || isSaving}
          className="bg-[#ff6b00] hover:bg-[#ff8533] text-white"
        >
          {isSaving ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Сохранить
            </>
          )}
        </Button>
        
        {value && (
          <Button
            variant="ghost"
            onClick={() => setIsEditing(false)}
            className="text-[#8b949e] hover:text-white"
          >
            Отмена
          </Button>
        )}
        
        <a
          href="https://steamcommunity.com/id/me/tradeoffers/privacy#trade_offer_access_url"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto text-sm text-[#66c0f4] hover:underline flex items-center gap-1"
        >
          Где взять?
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      <Alert className="bg-[#1a2332] border-[#30363d]">
        <AlertTriangle className="w-4 h-4 text-[#ff6b00]" />
        <AlertDescription className="text-[#8b949e]">
          Trade URL необходим для автоматической отправки предметов после покупки. 
          Без него вы не сможете получить купленные скины.
        </AlertDescription>
      </Alert>
    </div>
  );
}
